import { useEffect, useState } from "react";
import { auth } from "../firebase-config";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import {
  Anchor,
  Box,
  Button,
  Center,
  Container,
  Loader,
  LoadingOverlay,
  Paper,
  PasswordInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconLock, IconMail, IconMailOpened, IconAlertCircle } from "@tabler/icons";
import { ReactComponent as CellSoftwareLogo } from "../images/cell-software-logo.svg";
import * as apiService from "../api-service";
import { recordSignInMethod, SIGN_IN_METHOD } from "../components/AuthMethod";

// Where Firebase sends someone back to after they click the emailed sign-in link - has to be an
// address this same app is actually served from. Whatever domain that resolves to needs to be in
// Firebase Console > Authentication > Settings > Authorized domains already, or the link fails.
const EMAIL_LINK_REDIRECT_URL = `${window.location.origin}/`;

// Firebase's email link only carries a one-time code, not the email address it was sent to - on
// the same browser/device that requested it, remembering the email here lets clicking the link
// complete sign-in with no extra step. Opening it on a different device (nothing stored here)
// falls back to asking for the email again below.
const EMAIL_FOR_SIGN_IN_KEY = "emailForSignIn";

// Separate from the key above on purpose - that one is cleared the moment an email-link sign-in
// completes (it only ever means "a link is pending for this address"), so relying on it alone
// meant branding would disappear again the next time someone visited the login page, and a
// password sign-in never touched it at all. This one is written on every successful sign-in,
// regardless of which method was used, and is never cleared - so logging out and coming back
// still shows the right company's logo/colour straight away instead of the generic default until
// the email is retyped.
const LAST_SIGNED_IN_EMAIL_KEY = "lastSignedInEmail";

// Same fallback colour used app-wide (App.js, Notifications.js, dashboard.js) via the
// --app-accent-color CSS variable for whichever company is currently signed in - reused here so
// the login screen's branding (badge background, "check your email" icon) matches exactly once a
// company match is found, and falls back to the same default blue everywhere else does when none
// is (or hasn't loaded yet).
const DEFAULT_ACCENT_COLOR = "#228be6";

// How long to wait after someone stops typing in the email field before looking up branding for
// its domain - avoids firing a request on every keystroke while someone's still typing.
const BRANDING_LOOKUP_DEBOUNCE_MS = 400;

// Minimum gap between one "Send sign-in link" request and the next, regardless of whether it's to
// the same address or a different one typed in after "Use a different email" - purely a client-
// side guard against rapid repeat clicks tripping Firebase's own per-project send quota
// (auth/quota-exceeded), which otherwise has no visible cooldown/feedback of its own between
// attempts. Firebase's own sending can genuinely take a few minutes rather than being instant, so
// this is deliberately long enough to discourage an impatient re-send well before the first email
// could even have arrived, not just long enough to dodge the quota.
const RESEND_COOLDOWN_MS = 60_000;

// Shared by both the password and forgot-password flows - the mapping itself hasn't changed from
// before, just pulled out so the email-link flow below can reuse the same messaging instead of
// duplicating it a third time.
function describeAuthError(error) {
  const message = error?.message ?? "";
  if (message.includes("invalid-email") || message.includes("missing-email")) {
    return { field: "email", message: "Invalid email" };
  }
  // "No account with this email" and "wrong password for this email" are deliberately reported
  // identically - both the wording AND which field (`field: null` shows it as a page-level banner
  // rather than highlighting the email or password box specifically) - so nobody can tell, just
  // from the sign-in form's response, whether a given email address has an account here at all.
  // Distinguishing the two used to let anyone enumerate registered emails one guess at a time.
  if (
    message.includes("user-not-found") ||
    message.includes("wrong-password") ||
    message.includes("invalid-credential")
  ) {
    return { field: null, message: "Incorrect email or password." };
  }
  if (message.includes("too-many-requests")) {
    return {
      field: null,
      message:
        "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.",
    };
  }
  return { field: null, message: message || "Something went wrong. Please try again." };
}

// The same centered card shell every screen below renders into - logo banner, a Paper with a
// soft shadow - so switching between "sign in", "check your email", etc. never jumps the overall
// layout around, only the content inside the card. No heading text above the box on purpose -
// just the logo, sized to match the card underneath it, so it reads as one unified block rather
// than a logo sitting above a separate title.
//
// `branding` (nullable, see the debounced lookup in Login() below) is whichever company's logo/
// accent colour currently matches the email that's been typed in - once it resolves, the banner
// swaps from the generic Cell Software mark on a neutral background to that company's own
// uploaded logo sitting on their brand colour, so the very first thing someone sees confirms
// they're signing into the right place.
//
// `brandingLoading` covers the network round-trip to look branding up at all; once that resolves
// there's still the actual logo image itself to download before it can be shown, so `imgLoaded`
// tracks that separately - together they keep a spinner up for the whole time the badge would
// otherwise be sitting empty or (worse) flashing the generic default before swapping to the real
// logo a moment later.
function LoginShell({ branding, brandingLoading, children }) {
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    setImgLoaded(false);
  }, [branding?.logoUrl]);

  const showSpinner = brandingLoading || (branding?.logoUrl && !imgLoaded);

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
      })}
    >
      <Container size={420} sx={{ width: "100%" }}>
        <Box
          sx={(theme) => ({
            width: "100%",
            height: 100,
            marginBottom: theme.spacing.md,
            borderRadius: theme.radius.md,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: branding?.accent || (theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[1]),
            boxShadow: theme.shadows.sm,
            overflow: "hidden",
            transition: "background-color 200ms ease",
          })}
        >
          {showSpinner ? (
            <Loader size="sm" />
          ) : branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.name ?? "Company logo"}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
              style={{ maxWidth: "50%", maxHeight: "55%", objectFit: "contain", display: "block" }}
            />
          ) : (
            <CellSoftwareLogo height={36} style={{ display: "block" }} />
          )}
        </Box>
        <Paper withBorder shadow="md" p={30} radius="md">
          {children}
        </Paper>
      </Container>
    </Box>
  );
}

function Login() {
  useEffect(() => {
    document.title = "Cell Software Portal - Login";
  }, []);

  // "link" (send a passwordless sign-in link) or "password" (email + password, for anyone who
  // still needs it) - a SegmentedControl rather than two separate pages, so switching back and
  // forth never loses whatever's already been typed into the email field. Email link is the
  // default/first option - it's the preferred way in.
  const [mode, setMode] = useState("link");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  // 0 means "no cooldown in effect" - see RESEND_COOLDOWN_MS/sendSignInLink below.
  const [nextLinkSendAllowedAt, setNextLinkSendAllowedAt] = useState(0);

  // Email-link specific state. `linkSent` swaps the whole card over to a "check your email"
  // confirmation. `needsEmailConfirmation` covers the cross-device case - opening the link on a
  // browser that never requested it, where Firebase can't tell whose link this is without asking.
  const [linkSent, setLinkSent] = useState(false);
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);
  const [isCompletingLink, setIsCompletingLink] = useState(true);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState(null);

  // Whichever company's public branding (logo/accent colour/name) currently matches the email
  // that's been typed in - null until a match is found (or while there's nothing valid to look up
  // yet), see the debounced lookup below. `brandingLoading` is only true while that lookup request
  // is actually in flight (not during the debounce wait itself, which would otherwise flicker a
  // spinner on every keystroke).
  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);

  const form = useForm({
    initialValues: { email: "", password: "" },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid Email"),
      password: (value) => (mode === "password" && !/^\S+$/.test(value) ? "Password is empty" : null),
    },
  });

  const completeEmailLinkSignIn = (email) => {
    setIsCompletingLink(true);
    setConfirmEmailError(null);
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        recordSignInMethod(SIGN_IN_METHOD.EMAIL_LINK);
        window.localStorage.setItem(LAST_SIGNED_IN_EMAIL_KEY, email);
        window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
        // Strips the one-time code out of the URL first - otherwise a reload would immediately
        // try (and fail) to reuse a sign-in link that's already been consumed.
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload(false);
      })
      .catch((error) => {
        setIsCompletingLink(false);
        setNeedsEmailConfirmation(true);
        const message = error?.message ?? "";
        if (message.includes("invalid-action-code") || message.includes("expired-action-code")) {
          setConfirmEmailError("This sign-in link has expired or has already been used. Request a new one below.");
        } else if (message.includes("invalid-email")) {
          setConfirmEmailError("That doesn't look like the email this link was sent to. Please check and try again.");
        } else {
          setConfirmEmailError(message);
        }
      });
  };

  // If this page was just opened by clicking the link from the sign-in email, complete sign-in
  // straight away rather than showing the ordinary form at all.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setIsCompletingLink(false);
      return;
    }

    const storedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
    if (storedEmail) {
      completeEmailLinkSignIn(storedEmail);
    } else {
      setIsCompletingLink(false);
      setNeedsEmailConfirmation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returning visitor convenience - prefills the email field from whichever address last
  // successfully signed in on this device/browser (falling back to a pending email-link address,
  // if that's all that's around), so a company's branding can show up immediately - including
  // right after logging out - rather than only after someone starts typing again.
  useEffect(() => {
    const storedEmail =
      window.localStorage.getItem(LAST_SIGNED_IN_EMAIL_KEY) || window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
    if (storedEmail) {
      form.setFieldValue("email", storedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Looks up the signed-out company branding (logo/accent/name) for whatever's currently in the
  // email field, debounced so it only fires once someone's paused typing rather than on every
  // keystroke. Clears back to the generic default whenever the field no longer looks like a real
  // email at all (e.g. it's been emptied out).
  useEffect(() => {
    const email = form.values.email.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setBranding(null);
      setBrandingLoading(false);
      return;
    }

    const timeout = setTimeout(() => {
      setBrandingLoading(true);
      apiService
        .getCompanyBranding({ email })
        .then((data) => setBranding(data))
        .catch(() => setBranding(null))
        .finally(() => setBrandingLoading(false));
    }, BRANDING_LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.email]);

  // Keeps the shared --app-accent-color CSS variable (App.js, Notifications.js, dashboard.js all
  // already read this for whichever company is signed in) in sync with whatever's currently
  // matched here, so the "check your email" icon below and anything else keyed off that variable
  // stay visually consistent with the badge above rather than needing their own separate prop.
  useEffect(() => {
    document.documentElement.style.setProperty("--app-accent-color", branding?.accent || DEFAULT_ACCENT_COLOR);
  }, [branding]);

  const signInWithPassword = (email, password) => {
    setIsSubmitting(true);
    form.clearErrors();
    setLoginError(null);

    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        if (userCredential.user != null) {
          recordSignInMethod(SIGN_IN_METHOD.PASSWORD);
          window.localStorage.setItem(LAST_SIGNED_IN_EMAIL_KEY, email);
          window.location.reload(false);
        } else {
          setLoginError("Something went wrong signing you in. Please try again.");
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        const { field, message } = describeAuthError(error);
        if (field) {
          form.setFieldError(field, message);
        } else {
          setLoginError(message);
        }
      });
  };

  const sendResetEmail = () => {
    if (!/^\S+@\S+$/.test(form.values.email)) {
      form.setFieldError("email", "Enter a valid email to send a reset email to.");
      return;
    }
    setLoginError(null);
    sendPasswordResetEmail(auth, form.values.email)
      .then(() => {
        alert("Password reset email sent. Please check your spam folder.");
      })
      .catch((error) => {
        // "No account with this email" gets the exact same success message as an email that DOES
        // exist, rather than a distinct error - showing a different response here is the textbook
        // way a "forgot password" form leaks which email addresses are registered, one guess at a
        // time. Every other error (bad email format, quota, etc.) still surfaces normally.
        if (error?.message?.includes("user-not-found")) {
          alert("Password reset email sent. Please check your spam folder.");
          return;
        }
        const { field, message } = describeAuthError(error);
        if (field) {
          form.setFieldError(field, message);
        } else {
          setLoginError(message);
        }
      });
  };

  // A few error codes need wording specific to THIS flow rather than the shared describeAuthError
  // above - notably "too-many-requests" there is worded around failed login attempts/password
  // resets, which would be actively misleading here, and quota/domain errors are unique to sending
  // a link at all (describeAuthError has no idea what they mean, so they'd fall through to its
  // generic "Something went wrong" - technically honest, but not actionable).
  function describeSendLinkError(error) {
    const message = error?.message ?? "";
    if (message.includes("quota-exceeded")) {
      return "Too many sign-in emails have been sent recently. Please wait a while before requesting another.";
    }
    if (message.includes("unauthorized-continue-uri")) {
      return "This page isn't set up to send sign-in links yet. Please contact your admin.";
    }
    if (message.includes("too-many-requests")) {
      return "Too many attempts. Please wait a while before trying again.";
    }
    return describeAuthError(error).message;
  }

  const sendSignInLink = () => {
    const email = form.values.email.trim();
    if (!/^\S+@\S+$/.test(email)) {
      form.setFieldError("email", "Invalid Email");
      return;
    }

    const waitMs = nextLinkSendAllowedAt - Date.now();
    if (waitMs > 0) {
      setLoginError(`Please wait ${Math.ceil(waitMs / 1000)}s before requesting another link.`);
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);
    sendSignInLinkToEmail(auth, email, { url: EMAIL_LINK_REDIRECT_URL, handleCodeInApp: true })
      .then(() => {
        window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
        // Also remembered here (not just on successful completion) so this device still shows the
        // right branding next visit even if the link ends up being opened/completed elsewhere.
        window.localStorage.setItem(LAST_SIGNED_IN_EMAIL_KEY, email);
        setNextLinkSendAllowedAt(Date.now() + RESEND_COOLDOWN_MS);
        setIsSubmitting(false);
        setLinkSent(true);
      })
      .catch((error) => {
        setIsSubmitting(false);
        setLoginError(describeSendLinkError(error));
      });
  };

  // Briefly, while checking whether this page load is a returning sign-in link click at all -
  // avoids a flash of the ordinary login form for the split second before that check resolves.
  if (isCompletingLink) {
    return (
      <LoginShell branding={branding} brandingLoading={brandingLoading}>
        <Center py={40}>
          <Loader />
        </Center>
      </LoginShell>
    );
  }

  // Opened the sign-in link on a different browser/device than the one that requested it (or the
  // link's expired/already used) - Firebase needs the email confirmed again before it'll finish
  // signing in.
  if (needsEmailConfirmation) {
    return (
      <LoginShell branding={branding} brandingLoading={brandingLoading}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!/^\S+@\S+$/.test(confirmEmail)) {
              setConfirmEmailError("Invalid Email");
              return;
            }
            completeEmailLinkSignIn(confirmEmail);
          }}
        >
          <Stack spacing="sm">
            <Text size="sm" c="dimmed">
              Confirm your email to finish signing in.
            </Text>
            {confirmEmailError && (
              <Text size="sm" color="red">
                {confirmEmailError}
              </Text>
            )}
            <TextInput
              label="Email"
              placeholder="example@domain.com"
              icon={<IconMail size={16} />}
              value={confirmEmail}
              onChange={(event) => setConfirmEmail(event.currentTarget.value)}
              autoFocus
            />
            <Button type="submit" size="md" mt="sm">
              Confirm and sign in
            </Button>
          </Stack>
        </form>
      </LoginShell>
    );
  }

  // The link's been sent - swap the whole card over to a plain confirmation rather than leaving
  // the form (and a "Send link" button someone might click again) on screen.
  if (linkSent) {
    return (
      <LoginShell branding={branding} brandingLoading={brandingLoading}>
        <Stack align="center" spacing="xs" py="md">
          <IconMailOpened size={40} stroke={1.5} color="var(--app-accent-color, #228be6)" />
          <Title order={3} align="center">
            Check your email
          </Title>
          <Text c="dimmed" align="center" size="sm">
            We&apos;ve sent a sign-in link to <b>{form.values.email}</b>. Open it on this device to
            finish signing in.
          </Text>
          <Text c="dimmed" align="center" size="xs">
            You can close this page - clicking the link in the email will log you in.
          </Text>
          <Text c="dimmed" align="center" size="xs">
            It usually arrives within a couple of minutes, but can occasionally take longer -
            please wait a while before requesting another. If it still hasn&apos;t turned up,{" "}
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => {
                setLinkSent(false);
                setLoginError(null);
                setMode("password");
              }}
            >
              try signing in with your password
            </Anchor>{" "}
            instead.
          </Text>
          <Button
            variant="subtle"
            mt="sm"
            onClick={() => {
              setLinkSent(false);
              setLoginError(null);
            }}
          >
            Use a different email
          </Button>
        </Stack>
      </LoginShell>
    );
  }

  return (
    <LoginShell branding={branding} brandingLoading={brandingLoading}>
      <Stack spacing={16}>
        <LoadingOverlay visible={isSubmitting} overlayBlur={2} />

        <SegmentedControl
          fullWidth
          value={mode}
          onChange={(value) => {
            setMode(value);
            setLoginError(null);
            form.clearErrors();
          }}
          data={[
            { label: "Email link", value: "link" },
            { label: "Password", value: "password" },
          ]}
        />

        <form
          onSubmit={form.onSubmit((values) => {
            if (mode === "password") {
              signInWithPassword(values.email, values.password);
            } else {
              sendSignInLink();
            }
          })}
        >
          <Stack spacing="sm">
            <TextInput
              label="Email"
              placeholder="example@domain.com"
              icon={<IconMail size={16} />}
              {...form.getInputProps("email")}
            />

            {mode === "password" && (
              <PasswordInput
                label="Password"
                placeholder="Password"
                icon={<IconLock size={16} />}
                {...form.getInputProps("password")}
              />
            )}

            {loginError && (
              <Text size="sm" color="red" sx={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                <IconAlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                {loginError}
              </Text>
            )}

            <Button type="submit" size="md" mt="xs" disabled={isSubmitting}>
              {mode === "password" ? "Sign in" : "Send sign-in link"}
            </Button>

            {mode === "password" && (
              <Anchor component="button" type="button" size="sm" ta="center" c="dimmed" onClick={sendResetEmail}>
                Forgot password?
              </Anchor>
            )}

            {mode === "link" && (
              <Text size="xs" c="dimmed" align="center">
                No password needed - we&apos;ll email you a link that signs you straight in.
              </Text>
            )}
          </Stack>
        </form>
      </Stack>
    </LoginShell>
  );
}

export default Login;
